import { create } from 'zustand';
import type { ChainId } from '@pleco/shared';
import { CHAINS } from '@pleco/shared';
import { api } from './api.js';

export interface ChainCapabilities {
  /** EVM maps replay an indexed ledger; Solana maps are point-in-time. */
  timeTravel: boolean;
  fundingTraces: boolean;
  fullHolderScan: boolean;
}

export interface EnabledChain {
  id: ChainId;
  label: string;
  symbol: string;
  color: string;
  family: string;
  capabilities: ChainCapabilities;
}

interface ChainState {
  chains: EnabledChain[];
  defaultChain: ChainId;
  loaded: boolean;
  load: () => Promise<void>;
}

/**
 * Chains this deployment serves (server-side PLECO_CHAINS). Starts as
 * Solana-only, matching the server default, so first paint never flashes
 * chains the server will reject.
 */
const fallback: EnabledChain = {
  id: 'sol',
  label: CHAINS.sol.label,
  symbol: CHAINS.sol.symbol,
  color: CHAINS.sol.color,
  family: 'svm',
  capabilities: { timeTravel: false, fundingTraces: false, fullHolderScan: false },
};

export const useChainStore = create<ChainState>((set) => ({
  chains: [fallback],
  defaultChain: 'sol',
  loaded: false,
  async load() {
    try {
      const r = await api.chains();
      if (r.chains.length) set({ chains: r.chains, defaultChain: r.default ?? r.chains[0]!.id, loaded: true });
    } catch {
      /* keep the fallback; the API surfaces real errors elsewhere */
    }
  },
}));

export const isDemo = (address: string) => address.toLowerCase().startsWith('demo');

export function useChains() {
  const { chains, defaultChain } = useChainStore();
  const evm = chains.filter((c) => c.family === 'evm');
  return {
    chains,
    defaultChain,
    evmChains: evm.map((c) => c.id),
    hasEvm: evm.length > 0,
    hasSolana: chains.some((c) => c.id === 'sol'),
    isEnabled: (id: string) => chains.some((c) => c.id === id),
    capability: (id: ChainId, key: keyof ChainCapabilities) => chains.find((c) => c.id === id)?.capabilities[key] ?? false,
    /** "Solana" / "Solana, Ethereum and Base" — for copy. */
    summary: chains.map((c) => c.label).join(', ').replace(/, ([^,]*)$/, ' and $1'),
  };
}
